import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FaClock, FaUser, FaPlus, FaCheckCircle, FaExclamationTriangle, FaLocationArrow, FaTimes } from 'react-icons/fa';
import { CustomButton10, CustomButton10Ca, CustomButton10Co } from '../../Templates';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { supabase } from '../../data/supabaseClient';
import Spinner from '../../common/Spinner';

const WaitingStatus = ({ 
  ride, 
  currentUser, 
  otherUser, 
  userRole, 
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  const [timeLeft, setTimeLeft] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [extensionMinutes, setExtensionMinutes] = useState(5);
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showOtherNotHereDialog, setShowOtherNotHereDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const timerRef = useRef(null);

  // Check arrival status
  const isDriverUser = currentUser.id === ride.driver_id;
  const hasUserArrived = isDriverUser ? !!ride.driver_arrival_time : !!ride.passenger_arrival_time;
  const hasOtherArrived = isDriverUser ? !!ride.passenger_arrival_time : !!ride.driver_arrival_time;
  const userArrivedFirst = hasUserArrived && !hasOtherArrived;
  
  // Get the actual role strings without translation
  const actualUserRole = isDriverUser ? 'Driver' : 'Passenger';
  const actualOtherRole = isDriverUser ? 'Passenger' : 'Driver';

  // Calculate time left
  useEffect(() => {
    if (!ride.timer_started_at) return;

    const calculateTimeLeft = () => {
      const startTime = new Date(ride.timer_started_at);
      const now = new Date();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      const totalSeconds = (ride.meeting_duration_minutes || 3) * 60;
      const remaining = Math.max(0, totalSeconds - elapsedSeconds);
      
      setTimeLeft(remaining);
      
      // Timer expired
      if (remaining <= 0 && !timerExpired) {
        setTimerExpired(true);
      }
    };

    calculateTimeLeft();
    timerRef.current = setInterval(calculateTimeLeft, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [ride.timer_started_at, ride.meeting_duration_minutes, timerExpired]);

  // Get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          if (accuracy > 50) {
            reject(new Error('Location accuracy is too low. Please ensure GPS is enabled.'));
            return;
          }

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: new Date().toISOString(),
              address: data.display_name || 'Address not available'
            });
          } catch (error) {
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: new Date().toISOString(),
              address: 'Address lookup failed'
            });
          }
        },
        (error) => reject(error),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Handle confirming arrival (for the user who hasn't arrived yet)
  const handleConfirmArrival = async (withLocation = false) => {
    setIsConfirming(true);
    
    try {
      let locationProof = null;
      
      if (withLocation) {
        setGettingLocation(true);
        try {
          locationProof = await getCurrentLocation();
        } catch (locError) {
          console.error('Location error:', locError);
          addToast({
            type: 'warning',
            title: 'Location Not Available',
            description: locError.message || 'Could not get location, proceeding without proof'
          });
        }
        setGettingLocation(false);
      }

      // Update arrival time
      const updateData = {
        status: 'both_present',
        updated_at: new Date().toISOString()
      };

      if (isDriverUser) {
        updateData.driver_arrival_time = new Date().toISOString();
        if (locationProof) {
          updateData.driver_location_proof = locationProof;
        }
      } else {
        updateData.passenger_arrival_time = new Date().toISOString();
        if (locationProof) {
          updateData.passenger_location_proof = locationProof;
        }
      }

      const { data: updatedRide, error } = await supabase
        .from('rides')
        .update(updateData)
        .eq('id', ride.id)
        .select()
        .single();

      if (error) throw error;
      
      // Update local state with the new ride data
      if (updatedRide && onUpdate) {
        onUpdate(updatedRide);
      }

      // Create notification
      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      
      await createNotification(
        otherUserId,
        'both_present',
        'Both users are now present at the meeting point',
        ride.id
      );

      await addSystemMessage(
        `${userRole} has arrived - Both users are now present!` +
        (locationProof ? ' (location verified)' : '')
      );

      addToast({
        type: 'success',
        title: 'Both Present',
        description: 'Both users are now at the meeting point!'
      });

      setShowLocationDialog(false);
      
    } catch (error) {
      console.error('Error confirming arrival:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to confirm arrival'
      });
    } finally {
      setIsConfirming(false);
      setGettingLocation(false);
    }
  };

  // Handle "Other user not here" report
  const handleOtherNotHere = async (withLocation = false) => {
    setIsConfirming(true);
    
    try {
      let locationProof = null;
      
      if (withLocation) {
        setGettingLocation(true);
        try {
          locationProof = await getCurrentLocation();
        } catch (locError) {
          console.error('Location error:', locError);
          addToast({
            type: 'warning',
            title: t('ride_actions.location_not_available'),
            description: locError.message || t('ride_actions.location_not_available_desc')
          });
        }
        setGettingLocation(false);
      }

      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      
      // Create report
      const noShowReport = {
        reported_by: currentUser.id,
        reported_at: new Date().toISOString(),
        reporter_location_proof: locationProof,
        timer_remaining_seconds: timeLeft
      };

      const { data: updatedRide, error } = await supabase
        .from('rides')
        .update({
          status: actualUserRole === 'Driver' ? 'passenger_no_show' : 'driver_no_show',
          other_not_here_report: noShowReport,
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id)
        .select()
        .single();

      if (error) throw error;
      
      // Update local state with the new ride data
      if (updatedRide && onUpdate) {
        onUpdate(updatedRide);
      }

      await createNotification(
        otherUserId,
        'ride_no_show',
        `Ride cancelled - ${actualOtherRole} not at meeting point`,
        ride.id
      );

      await addSystemMessage(
        `Ride cancelled - ${actualOtherRole} was not at the meeting point` +
        (locationProof ? ' (location verified)' : '')
      );

      addToast({
        type: 'info',
        title: 'Ride Cancelled',
        description: `${actualOtherRole} marked as no-show`
      });

      setShowOtherNotHereDialog(false);
      
    } catch (error) {
      console.error('Error reporting no-show:', error);
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: t('ride_actions.failed_report_no_show')
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle ride cancellation (after timer expired)
  const handleCancelRide = async (withLocation = false) => {
    setIsConfirming(true);
    
    try {
      let locationProof = null;
      
      if (withLocation) {
        setGettingLocation(true);
        try {
          locationProof = await getCurrentLocation();
        } catch (locError) {
          console.error('Location error:', locError);
          addToast({
            type: 'warning',
            title: t('ride_actions.location_not_available'),
            description: locError.message || t('ride_actions.location_not_available_desc')
          });
        }
        setGettingLocation(false);
      }

      const updateData = {
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancellation_reason: 'timer_expired_user_cancelled',
        updated_at: new Date().toISOString()
      };

      if (locationProof) {
        updateData.cancellation_location_proof = locationProof;
      }

      const { data: updatedRide, error } = await supabase
        .from('rides')
        .update(updateData)
        .eq('id', ride.id)
        .select()
        .single();

      if (error) throw error;
      
      // Update local state with the new ride data
      if (updatedRide && onUpdate) {
        onUpdate(updatedRide);
      }

      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;

      await createNotification(
        otherUserId,
        'ride_cancelled',
        t('ride_actions.ride_cancelled_after_wait'),
        ride.id
      );

      await addSystemMessage(
        t('ride_actions.ride_cancelled_system') +
        (locationProof ? ' ' + t('ride_actions.location_proof_saved') : '')
      );

      addToast({
        type: 'info',
        title: t('ride_actions.ride_cancelled'),
        description: t('ride_actions.ride_cancelled_desc')
      });

      setShowCancelDialog(false);
      
    } catch (error) {
      console.error('Error cancelling ride:', error);
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: t('ride_actions.failed_cancel_ride')
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle extension request
  const handleExtensionRequest = async () => {
    setIsExtending(true);
    
    try {
      // Update total wait time
      const currentTotalWait = ride.total_wait_time_minutes || ride.meeting_duration_minutes || 3;
      const newTotalWait = currentTotalWait + extensionMinutes;
      
      // Check maximum wait time (30 minutes)
      if (newTotalWait > 30) {
        addToast({
          type: 'warning',
          title: t('ride_actions.max_wait_reached'),
          description: t('ride_actions.max_wait_30_minutes')
        });
        setIsExtending(false);
        return;
      }

      // Create extension request
      const extensionRequest = {
        requested_by: currentUser.id,
        requested_at: new Date().toISOString(),
        additional_minutes: extensionMinutes,
        status: 'pending'
      };

      // Add to existing extension requests or create new array
      const currentRequests = ride.extension_requests || [];
      const updatedRequests = [...currentRequests, extensionRequest];

      const { data: updatedRide, error } = await supabase
        .from('rides')
        .update({
          extension_requests: updatedRequests,
          wait_extension_count: (ride.wait_extension_count || 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id)
        .select()
        .single();

      if (error) throw error;
      
      // Update local state with the new ride data
      if (updatedRide && onUpdate) {
        onUpdate(updatedRide);
      }

      // Create notification for other user
      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      
      await createNotification(
        otherUserId,
        'extension_request',
        `${userRole} requests ${extensionMinutes} more minutes`,
        ride.id
      );

      await addSystemMessage(
        `${userRole} requested ${extensionMinutes} more minutes - waiting for approval`
      );

      addToast({
        type: 'info',
        title: 'Extension Requested',
        description: `Waiting for ${userRole === 'Driver' ? 'passenger' : 'driver'} to approve ${extensionMinutes} more minutes`
      });

      setShowExtensionDialog(false);
      
    } catch (error) {
      console.error('Error requesting extension:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to request extension'
      });
    } finally {
      setIsExtending(false);
    }
  };

  // Handle extension response
  const handleExtensionResponse = async (requestIndex, approve) => {
    setIsExtending(true);
    
    try {
      const extensionRequests = [...(ride.extension_requests || [])];
      const request = extensionRequests[requestIndex];
      
      if (approve) {
        // Approve the extension - correctly calculate the new duration
        const currentDuration = ride.meeting_duration_minutes || 3;
        const newDuration = currentDuration + request.additional_minutes;
        
        const updateData = {
          meeting_duration_minutes: newDuration,
          total_wait_time_minutes: (ride.total_wait_time_minutes || currentDuration) + request.additional_minutes,
          updated_at: new Date().toISOString()
        };

        // Mark request as approved and remove from pending
        extensionRequests[requestIndex].status = 'approved';
        extensionRequests[requestIndex].responded_at = new Date().toISOString();
        extensionRequests[requestIndex].responded_by = currentUser.id;
        updateData.extension_requests = extensionRequests;

        const { data: updatedRide, error } = await supabase
          .from('rides')
          .update(updateData)
          .eq('id', ride.id)
          .select()
          .single();

        if (error) throw error;
        
        // Update local state with the new ride data
        if (updatedRide && onUpdate) {
          onUpdate(updatedRide);
        }

        await addSystemMessage(
          `Timer extended by ${request.additional_minutes} minutes (approved by ${userRole})`
        );

        addToast({
          type: 'success',
          title: 'Extension Approved',
          description: `Timer extended by ${request.additional_minutes} minutes`
        });
      } else {
        // Decline the extension
        extensionRequests[requestIndex].status = 'declined';
        extensionRequests[requestIndex].responded_at = new Date().toISOString();
        extensionRequests[requestIndex].responded_by = currentUser.id;

        const { data: updatedRide, error } = await supabase
          .from('rides')
          .update({
            extension_requests: extensionRequests,
            updated_at: new Date().toISOString()
          })
          .eq('id', ride.id)
          .select()
          .single();

        if (error) throw error;
        
        // Update local state with the new ride data
        if (updatedRide && onUpdate) {
          onUpdate(updatedRide);
        }

        await addSystemMessage(
          `Extension request declined by ${userRole}`
        );

        addToast({
          type: 'info',
          title: 'Extension Declined',
          description: 'Extension request was declined'
        });
      }

      // Notify the requester
      await createNotification(
        request.requested_by,
        approve ? 'extension_approved' : 'extension_declined',
        approve ? 
          `Timer extended by ${request.additional_minutes} minutes` :
          `Extension request declined by ${userRole}`,
        ride.id
      );
      
    } catch (error) {
      console.error('Error responding to extension:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to respond to extension request'
      });
    } finally {
      setIsExtending(false);
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get pending extension requests
  const pendingExtensionRequests = (ride.extension_requests || []).filter(
    req => req.status === 'pending' && req.requested_by !== currentUser.id
  );

  // Check if current user has pending extension request
  const userHasPendingRequest = (ride.extension_requests || []).some(
    req => req.status === 'pending' && req.requested_by === currentUser.id
  );

  return (
    <div className="RAC-waiting-status">
      <div className="-RAC-waiting-content">
        {/* Timer Display */}
        <div className={`RAC-timer-display ${timeLeft <= 60 ? 'warning' : timeLeft <= 0 ? 'expired' : ''}`}>
          <FaClock size={24} />
          <span className="timer-text">
            {timeLeft > 0 ? formatTime(timeLeft) : 'Timer Expired'}
          </span>
        </div>

        {/* Pending Extension Requests */}
        {pendingExtensionRequests.map((request, index) => (
          <div key={index} className="extension-request">
            <div className="request-info">
              <FaPlus size={12} />
              <span>
                {userRole === 'Driver' ? 'Passenger' : 'Driver'} requests {request.additional_minutes} more minutes
              </span>
            </div>
            <div className="request-actions">
              <CustomButton10Ca
                onClick={() => handleExtensionResponse(
                  (ride.extension_requests || []).indexOf(request), 
                  false
                )}
                disabled={isExtending}
              >
                Decline
              </CustomButton10Ca>
              <CustomButton10Co
                onClick={() => handleExtensionResponse(
                  (ride.extension_requests || []).indexOf(request), 
                  true
                )}
                disabled={isExtending}
              >
                {isExtending ? <Spinner size="small" showText={false} /> : 'Approve'}
              </CustomButton10Co>
            </div>
          </div>
        ))}

        {/* Action Buttons */}
        <div className="waiting-actions">
          {!hasUserArrived ? (
            // User hasn't arrived yet
            <div className="not-arrived-actions">
              <CustomButton10Co
                onClick={() => setShowLocationDialog(true)}
                label="I'm Here"
                disabled={isConfirming}
              >
                {isConfirming ? <Spinner size="small" showText={false} /> : <FaLocationArrow />}
              </CustomButton10Co>
              
              {/* Request more time - shown to user who hasn't arrived */}
              {timeLeft > 0 && !userHasPendingRequest && (
                <CustomButton10
                  onClick={() => setShowExtensionDialog(true)}
                  label="Request More Time"
                  disabled={isExtending}
                >
                  <FaPlus />
                </CustomButton10>
              )}
              {userHasPendingRequest && (
                <span className="pending-request">Extension request pending...</span>
              )}
            </div>
          ) : (
            // User has arrived
            <div className="arrived-actions">
              {timeLeft > 0 ? (
                <>
                  <span className="arrived-message">
                    Waiting for {userRole === 'Driver' ? 'passenger' : 'driver'}...
                  </span>
                  
                  {/* Other user not here button - only for arrived user */}
                  <CustomButton10Ca
                    onClick={() => setShowOtherNotHereDialog(true)}
                    label={`${userRole === 'Driver' ? 'Passenger' : 'Driver'} not here`}
                    disabled={isConfirming}
                  >
                    <FaExclamationTriangle />
                  </CustomButton10Ca>
                </>
              ) : (
                // Timer expired - show cancel button inline
                <>
                  <span className="timer-expired-message">
                    {t('ride_actions.waiting_time_expired')}
                  </span>
                  <CustomButton10Ca
                    onClick={() => setShowCancelDialog(true)}
                    label={t('ride_actions.cancel_ride')}
                    disabled={isConfirming}
                  >
                    <FaTimes />
                  </CustomButton10Ca>
                  <CustomButton10
                    onClick={() => setShowExtensionDialog(true)}
                    label={t('ride_actions.wait_more')}
                    disabled={isExtending}
                  >
                    <FaPlus />
                  </CustomButton10>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Location Dialog */}
      {showLocationDialog && (
        <WaitingLocationDialog
          onCancel={() => setShowLocationDialog(false)}
          onConfirm={(withLocation) => handleConfirmArrival(withLocation)}
          isConfirming={isConfirming}
          gettingLocation={gettingLocation}
        />
      )}

      {/* Extension Dialog */}
      {showExtensionDialog && (
        <WaitingExtensionDialog
          onCancel={() => setShowExtensionDialog(false)}
          onConfirm={handleExtensionRequest}
          isExtending={isExtending}
          extensionMinutes={extensionMinutes}
          setExtensionMinutes={setExtensionMinutes}
        />
      )}

      {/* Other Not Here Dialog */}
      {showOtherNotHereDialog && (
        <OtherNotHereDialog
          onCancel={() => setShowOtherNotHereDialog(false)}
          onConfirm={(withLocation) => handleOtherNotHere(withLocation)}
          isConfirming={isConfirming}
          gettingLocation={gettingLocation}
          userRole={userRole}
        />
      )}

      {/* Cancel Ride Dialog */}
      {showCancelDialog && (
        <CancelRideDialog
          onCancel={() => setShowCancelDialog(false)}
          onConfirm={(withLocation) => handleCancelRide(withLocation)}
          isConfirming={isConfirming}
          gettingLocation={gettingLocation}
        />
      )}
    </div>
  );
};

// Waiting Location Dialog Component - Renders as fullscreen modal
const WaitingLocationDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  gettingLocation
}) => {
  const { t } = useTranslation('common');
  const [shareLocation, setShareLocation] = useState(false);

  return ReactDOM.createPortal(
    <div 
      className="RAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isConfirming) {
          onCancel();
        }
      }}
    >
      <div className="RAC-modal2-v2">
        <div className="RAC-location-dialog">
          <div className="RAC-dialog-content">
            <h4>{t('ride_actions.confirm_your_arrival')}</h4>
            <p>{t('ride_actions.let_other_know_arrived_short')}</p>
            
            <div className="RAC-location-option">
              <label className="RAC-checkbox-label">
                <input
                  type="checkbox"
                  checked={shareLocation}
                  onChange={(e) => setShareLocation(e.target.checked)}
                  disabled={isConfirming}
                />
                <span>{t('ride_actions.share_location_proof')}</span>
              </label>
              <p className="RAC-location-help">
                {t('ride_actions.optional_gps_proof')}
              </p>
            </div>
            
            <div className="RAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isConfirming}
              />
              <CustomButton10Co
                onClick={() => onConfirm(shareLocation)}
                disabled={isConfirming || gettingLocation}
              >
                {isConfirming || gettingLocation ? (
                  <Spinner size="small" showText={false} />
                ) : (
                  t('ride_actions.confirm_arrival')
                )}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Waiting Extension Dialog Component - Renders as fullscreen modal
const WaitingExtensionDialog = ({
  onCancel,
  onConfirm,
  isExtending,
  extensionMinutes,
  setExtensionMinutes
}) => {
  const { t } = useTranslation('common');

  return ReactDOM.createPortal(
    <div 
      className="RAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isExtending) {
          onCancel();
        }
      }}
    >
      <div className="RAC-modal2-v2">
        <div className="RAC-extension-request-dialog">
          <div className="RAC-dialog-content">
            <h4>{t('ride_actions.request_more_time')}</h4>
            <p>{t('ride_actions.how_many_additional_minutes')}</p>
            
            <div className="RAC-extension-options">
              <select
                className="RAC-duration-select-compact"
                value={extensionMinutes}
                onChange={(e) => setExtensionMinutes(parseInt(e.target.value))}
                disabled={isExtending}
              >
                <option value={3}>3 {t('ride_actions.minutes')}</option>
                <option value={5}>5 {t('ride_actions.minutes')}</option>
                <option value={10}>10 {t('ride_actions.minutes')}</option>
                <option value={15}>15 {t('ride_actions.minutes')}</option>
              </select>
            </div>
            
            <div className="RAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isExtending}
              />
              <CustomButton10Co
                onClick={onConfirm}
                disabled={isExtending}
              >
                {isExtending ? <Spinner size="small" showText={false} /> : t('ride_actions.request_extension')}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Other Not Here Dialog Component
const OtherNotHereDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  gettingLocation,
  userRole
}) => {
  const { t } = useTranslation('common');
  const [shareLocation, setShareLocation] = useState(false);

  return ReactDOM.createPortal(
    <div className="RAC-modal-overlay-v2">
      <div className="RAC-modal2-v2">
        <div className="RAC-dialog-content">
          <div className="RAC-safety-icon">
            <FaExclamationTriangle size={48} color="#EF4444" />
          </div>
          <h4>{t('ride_actions.report_no_show')}</h4>
          <p>
            {t('ride_actions.confirm_other_not_here', { 
              role: userRole === 'Driver' ? t('ride_actions.passenger').toLowerCase() : t('ride_actions.driver').toLowerCase() 
            })}
          </p>
          <p className="RAC-warning-text">
            {t('ride_actions.ride_will_be_cancelled_warning')}
          </p>
          
          <div className="RAC-location-option">
            <label className="RAC-checkbox-label">
              <input
                type="checkbox"
                checked={shareLocation}
                onChange={(e) => setShareLocation(e.target.checked)}
                disabled={isConfirming}
              />
              <span>{t('ride_actions.share_my_location_proof')}</span>
            </label>
            <p className="RAC-location-help">
              {t('ride_actions.location_helps_resolve')}
            </p>
          </div>
          
          <div className="RAC-dialog-actions">
            <CustomButton10
              onClick={onCancel}
              label={t('buttons.cancel')}
              disabled={isConfirming}
            />
            <CustomButton10Ca
              onClick={() => onConfirm(shareLocation)}
              disabled={isConfirming || gettingLocation}
            >
              {isConfirming || gettingLocation ? (
                <Spinner size="small" showText={false} />
              ) : (
                t('ride_actions.confirm_no_show')
              )}
            </CustomButton10Ca>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Cancel Ride Dialog Component
const CancelRideDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  gettingLocation
}) => {
  const { t } = useTranslation('common');
  const [shareLocation, setShareLocation] = useState(false);

  return ReactDOM.createPortal(
    <div className="RAC-modal-overlay-v2">
      <div className="RAC-modal2-v2">
        <div className="RAC-dialog-content">
          <h4>{t('ride_actions.cancel_ride_title')}</h4>
          <p>{t('ride_actions.waited_but_no_show')}</p>
          
          <div className="RAC-location-option">
            <label className="RAC-checkbox-label">
              <input
                type="checkbox"
                checked={shareLocation}
                onChange={(e) => setShareLocation(e.target.checked)}
                disabled={isConfirming}
              />
              <span>{t('ride_actions.share_location_was_here')}</span>
            </label>
            <p className="RAC-location-help">
              {t('ride_actions.location_proof_evidence')}
            </p>
          </div>
          
          <div className="RAC-dialog-actions">
            <CustomButton10
              onClick={onCancel}
              label={t('buttons.go_back')}
              disabled={isConfirming}
            />
            <CustomButton10Ca
              onClick={() => onConfirm(shareLocation)}
              disabled={isConfirming || gettingLocation}
            >
              {isConfirming || gettingLocation ? (
                <Spinner size="small" showText={false} />
              ) : (
                t('ride_actions.confirm_cancel')
              )}
            </CustomButton10Ca>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WaitingStatus;